import { useTranslation } from 'react-i18next';
import { useLockingEnabled } from '@/tools/models/useLockingEnabled.hook';
import { Icon } from '@seeqdev/qomponents';
import type React from 'react';
import type { ApplicationVersionInfo } from '@/tools/models/useTableModelVersionMetadata.hook';

interface ModelColumnHeaderProps {
  /** Model name supplied by ag-grid as the column-group header text. */
  displayName: string;
  versionInfo?: ApplicationVersionInfo;
  /** Invoked when the user activates the upgrade badge to re-apply the production version. */
  onUpgrade?: () => void;
}

const BADGE_CLASSES =
  'tw:px-1.5 tw:py-0.5 tw:text-[0.7rem] tw:font-semibold tw:rounded tw:whitespace-nowrap tw:bg-sq-theme-light tw:text-sq-theme-dark tw:ml-1';

const UPGRADE_BADGE_CLASSES = `${BADGE_CLASSES} tw:cursor-pointer`;

/**
 * ag-grid column-group header for model columns in a Scaling Table. Renders the model
 * name plus a "v{N}" badge when Locking is enabled and the application's referenced
 * version is known. When the application is outdated and an `onUpgrade` callback is
 * provided, also renders a clickable upgrade badge showing the production version.
 */
export const ModelColumnHeader = ({ displayName, versionInfo, onUpgrade }: ModelColumnHeaderProps) => {
  const { t } = useTranslation();
  const lockingEnabled = useLockingEnabled();
  const referencedVersionNumber = versionInfo?.referencedVersionNumber;
  const productionVersionNumber = versionInfo?.productionVersionNumber;
  const outdated =
    referencedVersionNumber !== undefined &&
    productionVersionNumber !== undefined &&
    productionVersionNumber > referencedVersionNumber;

  const handleUpgrade = (event: React.SyntheticEvent) => {
    // ag-grid attaches click handlers to column headers for sorting/menus; stop propagation so the upgrade action stays isolated.
    event.stopPropagation();
    onUpgrade?.();
  };

  return (
    <span data-testid="modelColumnHeader" className="flexColumnContainer flexCenter">
      <span>{displayName}</span>
      {lockingEnabled && referencedVersionNumber !== undefined && (
        <span className={BADGE_CLASSES} data-testid="modelColumnVersionLabel">
          <Icon icon="fa-lock" type="theme" extraClassNames="tw:mr-0.5 tw:text-[10px]" />
          {t('TEMPLATES.VERSION_LABEL', { version: referencedVersionNumber })}
        </span>
      )}
      {outdated && onUpgrade !== undefined && (
        <span
          className={UPGRADE_BADGE_CLASSES}
          data-testid="modelColumnUpgradeBadge"
          role="button"
          tabIndex={0}
          onClick={handleUpgrade}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleUpgrade(event);
            }
          }}>
          <Icon icon="fa-arrow-up" type="theme" extraClassNames="tw:mr-0.5 tw:text-[10px]" />
          {lockingEnabled
            ? t('TEMPLATES.UPGRADE_TO_PRODUCTION', { version: productionVersionNumber })
            : t('TEMPLATES.UPGRADE_AVAILABLE')}
        </span>
      )}
    </span>
  );
};
